import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';
import { Stakeholder, StakeholderEngagement } from '../types';

const STAKE_FIELDS = [
  'dagrun_pm_stakeholderid', 'dagrun_pm_name', 'dagrun_pm_userid', 'dagrun_pm_username',
  'dagrun_pm_role', 'dagrun_pm_influence', 'dagrun_pm_interest',
  'dagrun_pm_engagementstrategy', 'dagrun_pm_notes', 'dagrun_pm_projectid',
  'createdon',
];
const ENGAGE_FIELDS = [
  'dagrun_pm_stakeholderengagementid', 'dagrun_pm_name', 'dagrun_pm_stakeholderid',
  'dagrun_pm_engagementdate', 'dagrun_pm_type', 'dagrun_pm_summary',
  'dagrun_pm_loggedbyid', 'dagrun_pm_loggedbyname', 'createdon',
];

export async function getProjectStakeholders(pca: IPublicClientApplication, projectId: string): Promise<Stakeholder[]> {
  return dvList<Stakeholder>(pca, 'dagrun_pm_stakeholders', {
    select: STAKE_FIELDS,
    filter: `dagrun_pm_projectid eq '${projectId}'`,
    orderby: 'dagrun_pm_name asc',
    top: 500,
  });
}

export interface CreateStakeholderInput {
  projectId: string;
  name: string;
  userid?: string | null;
  username?: string | null;
  role?: string | null;
  influence?: number;
  interest?: number;
  engagementstrategy?: string | null;
  notes?: string | null;
}

export async function createStakeholder(pca: IPublicClientApplication, input: CreateStakeholderInput): Promise<string> {
  const body: Record<string, unknown> = {
    dagrun_pm_name: input.name,
    dagrun_pm_projectid: input.projectId,
  };
  if (input.userid)             body.dagrun_pm_userid = input.userid;
  if (input.username)           body.dagrun_pm_username = input.username;
  if (input.role)               body.dagrun_pm_role = input.role;
  if (input.influence != null)  body.dagrun_pm_influence = input.influence;
  if (input.interest != null)   body.dagrun_pm_interest = input.interest;
  if (input.engagementstrategy) body.dagrun_pm_engagementstrategy = input.engagementstrategy;
  if (input.notes)              body.dagrun_pm_notes = input.notes;
  return dvCreate(pca, 'dagrun_pm_stakeholders', body);
}

export async function updateStakeholder(pca: IPublicClientApplication, id: string, patch: Record<string, unknown>): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_stakeholders', id, patch);
}

export async function deleteStakeholder(pca: IPublicClientApplication, id: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_stakeholders', id);
}

// ── Engagements ────────────────────────────────────────────────────

export async function getStakeholderEngagements(pca: IPublicClientApplication, stakeholderId: string): Promise<StakeholderEngagement[]> {
  return dvList<StakeholderEngagement>(pca, 'dagrun_pm_stakeholderengagements', {
    select: ENGAGE_FIELDS,
    filter: `dagrun_pm_stakeholderid eq '${stakeholderId}'`,
    orderby: 'dagrun_pm_engagementdate desc',
    top: 200,
  });
}

export interface CreateEngagementInput {
  stakeholderId: string;
  engagementdate: string;  // ISO
  type: number;
  summary?: string | null;
  loggedbyid: string;
  loggedbyname: string;
}

export async function createEngagement(pca: IPublicClientApplication, input: CreateEngagementInput): Promise<string> {
  const typeLabel: Record<number, string> = {
    1: 'Meeting', 2: 'Email', 3: 'Call', 4: 'Report', 5: 'Other',
  };
  const body: Record<string, unknown> = {
    dagrun_pm_name: `${typeLabel[input.type] ?? 'Engagement'} — ${new Date(input.engagementdate).toLocaleDateString()}`,
    dagrun_pm_stakeholderid: input.stakeholderId,
    dagrun_pm_engagementdate: input.engagementdate,
    dagrun_pm_type: input.type,
    dagrun_pm_loggedbyid: input.loggedbyid,
    dagrun_pm_loggedbyname: input.loggedbyname,
  };
  if (input.summary) body.dagrun_pm_summary = input.summary;
  return dvCreate(pca, 'dagrun_pm_stakeholderengagements', body);
}

export async function deleteEngagement(pca: IPublicClientApplication, id: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_stakeholderengagements', id);
}
